import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { CookieService } from "ngx-cookie-service";
import { BehaviorSubject, Observable, of, tap, throwError, mergeMap } from 'rxjs';
import { environment } from "@env/environment";
import { AccountLicense, BusinessUserCreate, ILoginResult, IUser, UserType } from '@core/interfaces';
import * as config from '@core/configs/constant-urls';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

    public currentUserSubject: BehaviorSubject<IUser>;
    public currentUser: Observable<any>;
    public userActiveType$: BehaviorSubject<UserType | null> = new BehaviorSubject<UserType | null>(null);


    constructor(private http: HttpClient, private router: Router, private cookies: CookieService) {
        this.currentUserSubject = new BehaviorSubject<IUser>(JSON.parse(localStorage.getItem('userInfo')));
        this.currentUser = this.currentUserSubject.asObservable();
        this.userActiveType$.next(localStorage.getItem('userActiveType') ? localStorage.getItem('userActiveType') as UserType : this.currentUserSubject?.value?.userType);
    }

    public createUser(registrationForm: any) {
        return this.http.post<any>(environment.apiUrl+"api/customerProfile/Create", registrationForm)
          .pipe(
            mergeMap(data => {
              if (data?.succeeded) {
                this.router.navigate(["/info-confirm-email"], {queryParams: { email: registrationForm.email }});
                return of(data);
              }

              return throwError(() => data?.errors ?? data);
            })
          );
    }

    public createBusiness(registrationForm: BusinessUserCreate) {
        return this.http.post<any>(environment.apiUrl+"api/businessProfile/Create", registrationForm)
          .pipe(
            mergeMap(data => {
              if (data?.succeeded) {
                this.router.navigate(["/info-confirm-email"], {queryParams: { email: registrationForm.email }});
                return of(data);
              }

              return throwError(() => data?.errors ?? data);
            })
          );
    }

    public login(login: any) {
        return this.http.post<ILoginResult>(environment.apiUrl+"api/account/login", login)
          .pipe(
            tap( data => {
              if (data.success) {
                localStorage.setItem('userInfo', JSON.stringify(data.user));
                this.currentUserSubject.next(data.user);
                this.userActiveType$.next(data.user.userType)
              }
            })
          );
    }

    public refresh() {
      return this.http.post<ILoginResult>(environment.apiUrl+"api/account/refresh", {})
        .pipe(
          tap( data => {
            if (data.success) {
              localStorage.setItem('userInfo', JSON.stringify(data.user));
              this.currentUserSubject.next(data.user);
              this.userActiveType$.next(data.user.userType)
            }
            else{
              this.clearLocalSession();
            }
          })
        );
        
  }

    public confirmEmail(email: string, token: string) {
        const encodedEmail = encodeURIComponent(email);
        const encodedToken = encodeURIComponent(token);
        return this.http.get<any>(environment.apiUrl+`api/account/ConfirmEmail?email=${encodedEmail}&token=${encodedToken}`);

    }

  public sendConfirmationEmail(email: string): Observable<any> {
    return this.http.get<any>(environment.apiUrl+"api/Account/SendConfirmEmail?email=" + email);
  }

    public confirmAuth() {
        return this.http.get<any>(environment.apiUrl+"api/Admin/get");
    }

    public logout(): void {
        this.http.get<any>(environment.apiUrl+"api/account/logout").subscribe({
          next: () => {},
          error: () => {}
        });

        this.clearLocalSession();
        this.router.navigate(['/']);
    }

    public handleSessionExpired(): void {
      this.clearLocalSession();
      this.router.navigate(['/sign-in'], { queryParams: { externalError: 'session_expired' } });
    }

    public startGoogleAuth(operation: 'signin' | 'signup', options?: { redirectPath?: string }): void {
        const apiBase = (environment.apiUrl ?? '/').replace(/\/+$/, '');
        const oauthEndpoint = `${apiBase ? apiBase : ''}/api/oauth/google`;

        const callbackUrl = new URL(`${window.location.origin}/external-auth`);
        callbackUrl.searchParams.set('operation', operation);
        const redirectPath = options?.redirectPath;
        if (redirectPath) {
            callbackUrl.searchParams.set('redirect', redirectPath);
        }

        const url = `${oauthEndpoint}?operation=${operation}&returnUrl=${encodeURIComponent(callbackUrl.toString())}`;
        window.location.href = url;
    }

    public forgotPassword(data): Observable<any> {
      return this.http.post(`${environment.apiUrl}api/account/ForgotPassword`, data)
    }

    public resetPassword(data): Observable<any> {
      return this.http.post(`${environment.apiUrl}api/account/ResetPassword`, data)
    }

    public switchAccount(data): Observable<any> {
      return this.http.get(`${environment.apiUrl}api/Account/SwitchAccount`, {params: data})
        .pipe(
          tap( (res) => {
            localStorage.setItem('userInfo', JSON.stringify(res.user))
            this.userActiveType$.next(this.userActiveType$.getValue() === UserType.Business ? UserType.Customer : UserType.Business);
          })
        );
    }

    public updateCurrentUser(updatedData): void {
      this.currentUserSubject.next({...this.currentUserSubject.getValue(), ...updatedData});
      localStorage.setItem('userInfo', JSON.stringify(this.currentUserSubject.getValue()));
    }

    public get currentUserValue(): IUser {
        return this.currentUserSubject.value;
    }

    public get currentUserActiveType(): UserType {
      return this.userActiveType$.value;
    }

    private clearLocalSession(): void {
      localStorage.removeItem('userInfo');
      localStorage.removeItem('userActiveType');
      this.currentUserSubject.next(null);
      this.userActiveType$.next(null);
    }
}

